import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { User } from "firebase/auth";
import { GridCol } from "../table/MessageInfoTableList";
import { Spiner } from "../spinner/Spiner";
import { MessageAttendanceList, Messages, newRdbMessageAttendanceSearch, RdbMessageAttendanceMapping, RdbMessageAttendanceSearch } from "../../model/Message";
import { requestRdbMessagesAttendance } from "../../api/Messages.api";
import { useDispatch } from "react-redux";
import { alertOpen, AlertValue } from "../../features/alert.slice";
import TableList from "../table/TableList";
import { MessagingDeliveryDestinationController } from "../../controller/MessagingDeliveryDestinationController";
import { ReplyButton } from "../../model/ReplyButton";
import { AttendanceSearch } from "../../pages/messages/AttendanceSearch";

export type AttendanceResultTallyProp = {
  currentUser: User | null;
  message: Messages;
  replyButton: ReplyButton | undefined;
  replyButtonNames: string[];
  isListLoading: boolean;
  setIsListLoading: React.Dispatch<React.SetStateAction<boolean>>;
  addUserFlg: boolean;
  deleteUserList: MessageAttendanceList[];
  setDeleteUserList: React.Dispatch<React.SetStateAction<MessageAttendanceList[]>>;
  handleClickOpen: (data: MessageAttendanceList) => void
};

export const AttendanceResultTally = ({
  currentUser,
  message,
  replyButton,
  replyButtonNames,
  isListLoading,
  setIsListLoading,
  addUserFlg,
  deleteUserList,
  setDeleteUserList,
  handleClickOpen
}: AttendanceResultTallyProp) => {
  const dispatch = useDispatch();
  const messageDeliveryDestination = new MessagingDeliveryDestinationController();

  const [isLoad, setIsLoad] = useState<boolean>(true);
  const delayLoadFlg = useRef<boolean>(addUserFlg);

  const [buttonChange, setButtonChange] = useState<boolean>(false);
  const [checkChange, setCheckChange] = useState<boolean>(false);
  const [changeAttendanceData, setChangeAttendanceData] = useState<MessageAttendanceList | null>(null);
  const [newAttendanceList, setNewAttendanceList] = useState<MessageAttendanceList[]>([]);
  
  const [messageAttendanceList, setMessageAttendanceList] = useState<MessageAttendanceList[]>([]);
  const [replyButtonCount, setReplyButtonCount] = useState<Map<string, number>>(new Map());
  const [unreplyNum, setUnreplyNum] = useState<number>(0);
  const [attendanceButtonCount, setAttendanceButtonCount] = useState<Map<string, number>>(new Map());
  const [targetNum, setTargetNum] = useState<number | null>(null);
  const [undistributedNum, setUndistributedNum] = useState<number>(0);
  const [confirmationNum, setConfirmationNum] = useState<number>(0);
  const [unconfirmationNum, setUnconfirmationNum] = useState<number>(0);
  const [unattendanceNum, setUnattendanceNum] = useState<number>(0);

  const [gridRowLength, setGridRowLength] = useState<number>(0);
  const [selectPage, setSelectPage] = useState<number>(1);
  const [selectNumber, setSelectNumber] = useState(50);
  const [firstPageFlg, setFirstPageFlg] = useState(false);
  const [searchCondition, setSearchCondition] = useState<RdbMessageAttendanceSearch>(newRdbMessageAttendanceSearch());

  // 出欠一覧を取得する共通処理
  useEffect(() => {
    const f = async () => {
      if (message) {
        if (deleteUserList.length > 0) {
          setIsListLoading(true);
        }
        if (deleteUserList.length > 0 || delayLoadFlg.current) {
          // RDB反映待ちのため2秒後に出欠一覧取得
          setTimeout(async () => {
            await getMessagesAttendance();
            setDeleteUserList([]);
            delayLoadFlg.current = false;
            setIsLoad(false);
            setIsListLoading(false);
          }, 2000);
        } else {
          await getMessagesAttendance();
          setIsLoad(false);
          setIsListLoading(false);
        }
      }
    }
    f();
  }, [message, selectPage, selectNumber, searchCondition, deleteUserList]);

  async function getMessagesAttendance() {
    const result = await requestRdbMessagesAttendance(
      currentUser!.tenantId ?? '',
      message.id,
      message.select ?? '',
      selectNumber.toString(),
      selectPage.toString(),
      searchCondition
    );
    if (result.success) {
      const attendanceList = result.data.rows.map((d: any) => RdbMessageAttendanceMapping(d));
      setMessageAttendanceList(attendanceList);
      messageDeliveryDestination.replyResults(
        result.data.replyCount,
        replyButtonNames,
        setReplyButtonCount,
        setUnreplyNum
      );
      if (targetNum === null) {
        messageDeliveryDestination.attendanceResults(
          result.data.attendanceCount,
          replyButtonNames,
          setTargetNum,
          setUndistributedNum,
          setConfirmationNum,
          setUnconfirmationNum,
          setAttendanceButtonCount,
          setUnattendanceNum
        );
      }
      setGridRowLength(result.data.total);
    } else {
      console.log(result.data)
      dispatch(alertOpen(AlertValue("出欠一覧の取得に失敗しました。", "danger")));
    }
  }

  // 最大文字数を計算
  const maxLength = Math.max(
    ...replyButtonNames.map(name => {
      if (name === "会場で参加") return "会場".length;
      if (name === "オンラインで参加") return "オンライン".length;
      return name.length;
    }),
    "未回答".length,
  );

  const gridRow = useMemo(() => 
    messageDeliveryDestination.attendanceData(
      message,
      replyButton,
      messageAttendanceList,
      buttonChange,
      checkChange,
      handleClickOpen,
      setMessageAttendanceList,
      setButtonChange,
      setCheckChange,
      setChangeAttendanceData,
      setNewAttendanceList
    )
  , [messageAttendanceList]);

  useEffect(() => {
    const updated = new Map(attendanceButtonCount);

    newAttendanceList.forEach((newData, index) => {
      const newAttendance = newData.attendanceData;
      const newCheckbox = newData.checkbox;
      if (changeAttendanceData && newData.userId === changeAttendanceData.userId) {
        const oldAttendance = changeAttendanceData.attendanceData;
        const oldCheckbox = changeAttendanceData.checkbox;

        // 未回答の増減
        if (oldAttendance === '未回答' && newAttendance !== '未回答') {
          // 未回答 → 回答
          setUnattendanceNum(unattendanceNum - 1);
        } else if (oldAttendance !== '未回答' && newAttendance === '未回答') {
          // 回答 → 未回答
          setUnattendanceNum(unattendanceNum + 1);
        }

        // ボタン集計の差分（旧値を-1、新値を+1）
        if (oldAttendance !== newAttendance) {
          if (oldAttendance !== '') {
            updated.set(oldAttendance, (updated.get(oldAttendance) ?? 0) - 1);
          }
          if (newAttendance !== '') {
            updated.set(newAttendance, (updated.get(newAttendance) ?? 0) + 1);
          }
        }
        setAttendanceButtonCount(updated);

        // 確認数の増減
        if (oldCheckbox === false && newCheckbox === true) {
          // 未チェック → チェック
          setConfirmationNum(confirmationNum + 1);
          setUnconfirmationNum(unconfirmationNum - 1);
        } else if (oldCheckbox === true && newCheckbox === false) {
          // チェック → 未チェック
          setConfirmationNum(confirmationNum - 1);
          setUnconfirmationNum(unconfirmationNum + 1);
        }
      }
    })
  }, [changeAttendanceData, newAttendanceList]);

  useEffect(() => {
    const f = async () => {
      deleteUserList.forEach((delUser) => {
        const updated = new Map(attendanceButtonCount);
        setNewAttendanceList(prev => prev.filter(item => item.userId !== delUser.userId));
        
        // 取り消したユーザ
        if (delUser.attendanceData === '未回答') {
          setUnattendanceNum(unattendanceNum - 1);
        }
        if (delUser.attendanceData !== '') {
          updated.set(delUser.attendanceData, (updated.get(delUser.attendanceData) ?? 0) - 1);
        }
        setAttendanceButtonCount(updated);
        if (delUser.checkbox === false) {
          setUnconfirmationNum(unconfirmationNum - 1);
        } else if (delUser.checkbox === true) {
          // チェック → 未チェック
          setConfirmationNum(confirmationNum - 1);
        }
        setUndistributedNum(undistributedNum - 1);
      })
    }
    f();
  }, [deleteUserList]);

  // 数値の最大桁数を計算
  const maxCheckAttendanceLength = Math.max(
    (targetNum ?? 0 - undistributedNum).toString().length,
    undistributedNum.toString().length,
    confirmationNum.toString().length,
    (targetNum ?? 0 - confirmationNum).toString().length
  );
  const maxNumAttendanceLength = Math.max(
    ...Array.from(attendanceButtonCount.values()).map(num => num !== undefined ? num.toString().length : 0),
    unattendanceNum.toString().length,
    confirmationNum.toString().length,
    (targetNum ?? 0 - confirmationNum).toString().length
  );
  const maxNumReplyLength = Math.max(
    ...Array.from(replyButtonCount.values()).map(num => num !== undefined ? num.toString().length : 0),
    unreplyNum.toString().length,
  );

  // 桁数に応じてスペースを追加する関数
  const padNumber = (num: number, maxLength: number) => {
    const numStr = num.toString();
    const paddingLength = maxLength - numStr.length;
    const padding = paddingLength > 0 ? '0'.repeat(paddingLength) : '';
    const whitePadding = padding.replace(/0/g, '<span style="color: white;">&nbsp;&nbsp;</span>');
    return whitePadding + numStr;
  };

  // 検索ボタンが押されたときの処理
  const handleSearch = (condition: any) => {
    setIsLoad(true);
    setSearchCondition(condition);
    setSelectPage(1);
  };

  // ページング処理
  const pagingFunc = useCallback((page: number) => {
    setFirstPageFlg(false);
    setSelectPage(page);
  }, []);

  const gridCol: GridCol[] = messageDeliveryDestination.attendanceGridCol;

  const selectNumberList =
    [
      { id: 50, name: "50" },
      { id: 100, name: "100" },
      { id: 200, name: "200" },
    ];

  return (
    <>
      <div className="flex py-2">
        <div className="flex flex-1 flex-col pr-40">
          <div className="flex gap-x-10 min-w-max">
            <div className="flex flex flex-col">
              <span className="text-lg font-bold text-gray-900">■出欠状況</span>
              <div className="ml-5">
                <div dangerouslySetInnerHTML={{ __html: `対象者合計：${padNumber(targetNum ?? 0, maxCheckAttendanceLength)}名` }}></div>
                {undistributedNum > 0 && (
                  <div dangerouslySetInnerHTML={{ __html: `　（配信外：${padNumber(undistributedNum, maxCheckAttendanceLength)}名）` }}></div>
                )}
                <div dangerouslySetInnerHTML={{ __html: `確認済　　：${padNumber(confirmationNum, maxCheckAttendanceLength)}名` }}></div>
                <div dangerouslySetInnerHTML={{ __html: `未確認　　：${padNumber(unconfirmationNum, maxCheckAttendanceLength)}名` }}></div>
              </div>
            </div>
            <div className="flex flex flex-col mt-7 pr-40">
              {replyButtonNames.map((name) => {
                const num = attendanceButtonCount.get(name) ?? 0;
                let displayName = name;
                if (name === "会場で参加") displayName = "会場";
                if (name === "オンラインで参加") displayName = "オンライン";
                return name !== undefined ? (
                  <div key={name} dangerouslySetInnerHTML={{ __html: `${displayName.padEnd(maxLength, '　')}：${padNumber(num, maxNumAttendanceLength)}名` }}></div>
                ) : null;
              })}
              <div dangerouslySetInnerHTML={{ __html: `${"未回答".padEnd(maxLength, '　')}：${padNumber(unattendanceNum, maxNumAttendanceLength)}名` }}></div>
            </div>
            <div className="flex flex flex-col">
              <span className="text-lg font-bold text-gray-900">■返信状況</span>
              <div className="ml-5">
                <div dangerouslySetInnerHTML={{ __html: `対象者合計：${padNumber(targetNum ?? 0, maxNumReplyLength)}名` }}></div>
              </div>
            </div>
            <div className="flex flex flex-col mt-7">
              {replyButtonNames.map((name) => {
                const num = replyButtonCount.get(name) ?? 0;
                let displayName = name;
                if (name === "会場で参加") displayName = "会場";
                if (name === "オンラインで参加") displayName = "オンライン";
                return name !== undefined ? (
                  <div key={name} dangerouslySetInnerHTML={{ __html: `${displayName.padEnd(maxLength, '　')}：${padNumber(num, maxNumReplyLength)}名` }}></div>
                ) : null;
              })}
              <div dangerouslySetInnerHTML={{ __html: `${"未回答".padEnd(maxLength, '　')}：${padNumber(unreplyNum, maxNumReplyLength)}名` }}></div>
            </div>
          </div>
        </div>
      </div>
      <div className="pb-4">
        <AttendanceSearch
          message={message}
          replyButton={replyButton}
          onSearch={handleSearch}
        />
      </div>
      <div className="relative">
        {isLoad ?
          <Spiner />
          :
          <TableList
            header
            footer={false}
            gridCol={gridCol}
            gridRow={gridRow}
            gridRowLength={gridRowLength}
            limit={selectNumber}
            selectNumberList={selectNumberList}
            setSelectNumber={setSelectNumber}
            pagingFunc={pagingFunc}
            firstPageFlg={firstPageFlg}
            setFirstPageFlg={setFirstPageFlg}
            minWidth={"min-w-[900px]"}
            fixedHeight={true}
          />
        }
        {isListLoading && (
          <div className="absolute inset-0 bg-gray-100 bg-opacity-50 flex items-center justify-center">
            <Spiner />
          </div>
        )}
      </div>
    </>
  );
};
